import React from "react";
import { Link } from "react-router-dom";
import {
  Search,
  MapPin,
  Bell,
  ShieldCheck,
  MessageCircle,
  Users,
  Camera,
  Sparkles,
  PawPrint,
  ArrowRight,
  Heart,
} from "lucide-react";
import DownloadStores from "../components/DownloadStores";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_encontre-pets/artifacts/gjefocwz_Feature%20Graphics.PNG";

const Stat = ({ value, label, testId }) => (
  <div
    className="flex flex-col items-start"
    data-testid={testId}
  >
    <div className="font-display font-bold text-3xl sm:text-4xl brand-gradient-text">
      {value}
    </div>
    <div className="text-xs sm:text-sm text-[#3c1e6b]/65 mt-1 font-medium">
      {label}
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc, accent = "purple", testId }) => (
  <div
    data-testid={testId}
    className="feature-card relative rounded-3xl bg-white border border-[#7c3aed]/12 p-7 shadow-[0_10px_30px_-15px_rgba(124,58,237,0.25)]"
  >
    <div
      className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-5 ${
        accent === "teal" ? "icon-bubble-teal" : "icon-bubble"
      }`}
    >
      <Icon size={26} strokeWidth={2.2} />
    </div>
    <h3 className="font-display font-semibold text-lg text-[#2e1065] mb-2">
      {title}
    </h3>
    <p className="text-sm text-[#3c1e6b]/70 leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ num, title, desc, testId }) => (
  <div className="relative" data-testid={testId}>
    <div className="step-circle w-14 h-14 rounded-full flex items-center justify-center font-display font-bold text-[#6d28d9] text-xl mb-4">
      {num}
    </div>
    <h3 className="font-display font-semibold text-lg text-[#2e1065] mb-2">
      {title}
    </h3>
    <p className="text-sm text-[#3c1e6b]/70 leading-relaxed">{desc}</p>
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden brand-bg">
        <div className="absolute inset-0 paw-pattern opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="fade-up">
            <div className="mb-8" id="baixar">
              <DownloadStores testId="hero-download-stores" />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 border border-[#7c3aed]/15 text-xs sm:text-sm font-semibold text-[#6d28d9] mb-6 shadow-sm">
              <Sparkles size={14} className="text-[#14B8A6]" />
              Mapa em tempo real e comunidade que ajuda
            </div>

            <h1
              className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2e1065]"
              data-testid="hero-title"
            >
              Reconectando
              <br />
              <span className="brand-gradient-text">corações e patas.</span>
            </h1>

            <p
              className="mt-6 text-base sm:text-lg text-[#3c1e6b]/75 leading-relaxed max-w-xl"
              data-testid="hero-subtitle"
            >
              Pets · Perdidos e Achados é o aplicativo que conecta pessoas que
              perderam seus animais com quem os encontrou — com mapa em tempo
              real, alertas por localização e uma comunidade que ajuda.
            </p>

            <div className="mt-6">
              <Link
                to="/sobre"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#6d28d9] hover:text-[#5B21B6] transition-colors"
              >
                Conheça o app
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat value="Grátis" label="Disponível na App Store" testId="hero-stat-1" />
              <Stat value="24/7" label="Alertas em tempo real" testId="hero-stat-2" />
              <Stat value="100%" label="Comunidade colaborativa" testId="hero-stat-3" />
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative fade-up fade-up-2">
            <div className="absolute -inset-10 bg-gradient-to-br from-[#a855f7]/20 via-[#14b8a6]/10 to-transparent blur-3xl rounded-full pointer-events-none" />
            <div className="relative float-soft">
              <img
                src={HERO_IMAGE}
                alt="Pets Perdidos e Achados - aplicativo para reencontrar pets"
                className="relative w-full h-auto rounded-3xl shadow-[0_30px_80px_-30px_rgba(124,58,237,0.45)]"
                data-testid="hero-image"
              />
            </div>

            {/* Floating badges */}
            <div className="hidden sm:flex absolute -left-4 top-1/4 glass-card rounded-2xl px-4 py-3 shadow-lg items-center gap-2.5">
              <div className="w-9 h-9 rounded-full icon-bubble-teal flex items-center justify-center text-white">
                <MapPin size={16} />
              </div>
              <div>
                <div className="text-xs font-semibold text-[#2e1065]">Mapa em tempo real</div>
                <div className="text-[10px] text-[#3c1e6b]/60">Veja ocorrências próximas</div>
              </div>
            </div>
            <div className="hidden sm:flex absolute -right-2 bottom-12 glass-card rounded-2xl px-4 py-3 shadow-lg items-center gap-2.5">
              <div className="w-9 h-9 rounded-full icon-bubble flex items-center justify-center text-white">
                <Heart size={16} className="fill-white" />
              </div>
              <div>
                <div className="text-xs font-semibold text-[#2e1065]">Reencontros felizes</div>
                <div className="text-[10px] text-[#3c1e6b]/60">A cada compartilhamento</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FEATURES ============== */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
              <PawPrint size={14} /> Recursos do app
            </span>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]"
              data-testid="features-title"
            >
              Tudo que você precisa para
              <br />
              <span className="brand-gradient-text">trazer seu pet de volta.</span>
            </h2>
            <p className="mt-5 text-base text-[#3c1e6b]/70 leading-relaxed">
              Uma plataforma pensada para agilizar buscas, conectar pessoas e
              transformar cada compartilhamento em uma chance real de
              reencontro.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              testId="feature-search"
              icon={Search}
              title="Procure pets perto de você"
              desc="Veja fotos, descrições, raça, porte, cor e última localização de pets perdidos e encontrados próximos da sua região."
            />
            <FeatureCard
              testId="feature-publish"
              icon={Camera}
              accent="teal"
              title="Publique em poucos segundos"
              desc="Cadastre um pet perdido, encontrado ou avistado com foto, data, características e contato. Rápido, simples e completo."
            />
            <FeatureCard
              testId="feature-map"
              icon={MapPin}
              title="Mapa interativo de ocorrências"
              desc="Marque a última localização, registre avistamentos e acompanhe pontos próximos em tempo real direto no mapa."
            />
            <FeatureCard
              testId="feature-alerts"
              icon={Bell}
              accent="teal"
              title="Alertas por localização"
              desc="Receba avisos automáticos sempre que algum pet for registrado próximo de você. Tempo é tudo em uma busca."
            />
            <FeatureCard
              testId="feature-chat"
              icon={MessageCircle}
              title="Conversa direta no app"
              desc="Troque mensagens, fotos e pistas com outros usuários sem sair da plataforma — combinando resgates com segurança."
            />
            <FeatureCard
              testId="feature-community"
              icon={Users}
              accent="teal"
              title="Comunidade colaborativa"
              desc="Milhares de pessoas dispostas a ajudar: compartilhando posts, registrando avistamentos e participando dos resgates."
            />
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="relative py-20 sm:py-24 bg-gradient-to-b from-[#f5edff] to-[#faf7ff] border-y border-[#7c3aed]/8">
        <div className="absolute inset-0 paw-pattern opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
              <Sparkles size={14} /> Como funciona
            </span>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]"
              data-testid="how-it-works-title"
            >
              Três passos para um <span className="brand-gradient-text">reencontro</span>.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8 sm:gap-10 relative">
            <Step
              num="1"
              testId="step-1"
              title="Cadastre ou procure"
              desc="Publique seu pet desaparecido com foto e detalhes, ou pesquise por animais perdidos e encontrados perto de você."
            />
            <Step
              num="2"
              testId="step-2"
              title="Receba alertas e dicas"
              desc="A comunidade compartilha avistamentos no mapa e te avisa em tempo real sobre qualquer pista próxima."
            />
            <Step
              num="3"
              testId="step-3"
              title="Reencontre quem você ama"
              desc="Converse direto no app, combine o resgate em segurança e celebre mais uma volta pra casa."
            />
          </div>
        </div>
      </section>

      {/* ============== TRUST / WHY ============== */}
      <section className="relative py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
              <ShieldCheck size={14} /> Por que confiar
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]">
              Cada compartilhamento <br />
              <span className="brand-gradient-text">pode ser o reencontro.</span>
            </h2>
            <p className="mt-5 text-base text-[#3c1e6b]/70 leading-relaxed max-w-xl">
              Muitos pets desaparecem todos os dias e a maior dificuldade é
              divulgar rápido. O app centraliza anúncios, mapa, mensagens e
              comunidade em um único lugar — criando uma experiência simples
              mesmo para quem não tem familiaridade com tecnologia.
            </p>

            <ul className="mt-8 space-y-3.5" data-testid="why-list">
              {[
                "Interface moderna, simples e intuitiva",
                "Foco exclusivo em pets perdidos e encontrados",
                "Publicações rápidas e busca por região",
                "Privacidade e segurança em todas as conversas",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-[#14B8A6]/15 text-[#0d9488] flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-[#3c1e6b]/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 bg-gradient-to-tr from-[#7c3aed]/15 via-transparent to-[#14b8a6]/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=600&auto=format&fit=crop&q=70"
                alt="Cachorro feliz"
                className="rounded-3xl shadow-xl aspect-[3/4] object-cover row-span-2"
                data-testid="trust-image-dog"
              />
              <img
                src="https://images.unsplash.com/photo-1592194996308-7b43878e84a6?w=600&auto=format&fit=crop&q=70"
                alt="Gato com olhar atento"
                className="rounded-3xl shadow-xl aspect-square object-cover"
                data-testid="trust-image-cat-1"
              />
              <img
                src="https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=600&auto=format&fit=crop&q=70"
                alt="Pet reencontrando família"
                className="rounded-3xl shadow-xl aspect-square object-cover"
                data-testid="trust-image-cat-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="relative py-20 sm:py-24" data-testid="final-cta-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] p-10 sm:p-16 text-center" style={{
            background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 45%, #14b8a6 130%)",
          }}>
            <div className="absolute inset-0 paw-pattern opacity-30 pointer-events-none mix-blend-overlay" />
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-[#14b8a6]/30 rounded-full blur-3xl" />

            <div className="relative">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-xs font-semibold text-white">
                <PawPrint size={14} /> Disponível para Android e iOS
              </span>
              <h2 className="mt-5 font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Junte-se a quem está <br className="hidden sm:block" />
                trazendo pets pra casa.
              </h2>
              <p className="mt-5 text-white/85 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                Baixe agora e participe da maior rede colaborativa de
                localização de pets do Brasil.
              </p>

              <div className="mt-8 max-w-2xl mx-auto">
                <DownloadStores
                  variant="onDark"
                  testId="final-cta-download-stores"
                />
              </div>

              <div className="mt-6">
                <Link
                  to="/sobre"
                  data-testid="final-cta-about-btn"
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white font-semibold text-sm underline underline-offset-4 decoration-white/40"
                >
                  Saber mais sobre o app
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
